import { useEffect, useState } from 'react';

import styled from '@emotion/styled';
// import { CardKPI } from '@mesh.ai/rabbit';
import { MetricCards } from 'Deer/metricCards/MetricCards';
import { MODULE_KEYS_METRIC_CARDS_DEER } from 'Deer/metricCards/constants';
import Carousel from 'components/Carousel';
import { useSelectedView } from 'reducers/viewsSlice';

import { snakeCaseToTitleCase } from 'constants/helper';

import { getAggregatedCount } from 'services/analytics/review';

import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';

import { getCardIdFromStatType } from '../ReviewManageSummaryIntent/ReviewManageSummaryIntent';

const StyledCarousel = styled(Carousel)`
  padding: 8px 0px;
`;

const SUMMARY_SCORE_TYPE_ENUMS = {
  submitted: 'SUBMITTED',
  noRating: 'No rating',
  inProgress: 'IN_PROGRESS',
};

function createSummaryCardsData(summaryStats) {
  const summaryCardsData = [];

  function getScoreTypes(scores) {
    const scoreDetails = {};

    scores.forEach((score) => {
      switch (score.key) {
        case SUMMARY_SCORE_TYPE_ENUMS.submitted: {
          scoreDetails.submitted = {
            ...score,
          };
          break;
        }
        case SUMMARY_SCORE_TYPE_ENUMS.noRating: {
          scoreDetails.noRating = {
            ...score,
          };
          break;
        }
        case SUMMARY_SCORE_TYPE_ENUMS.inProgress: {
          scoreDetails.inProgress = {
            ...score,
          };
          break;
        }
        default:
          break;
      }
    });

    return scoreDetails;
  }

  function getSummaryChartData(scoreTypes) {
    const chartData = [
      { name: 'Submitted', value: scoreTypes.submitted?.count || 0 },
      { name: 'Pending', value: scoreTypes.inProgress?.count || 0 },
    ];

    return chartData;
  }

  summaryStats.forEach((stat) => {
    const scoreTypes = getScoreTypes(stat.scores);
    const summaryCardData = {
      averageScore: stat?.rating || '-',
      title: snakeCaseToTitleCase(stat.type),
      ...scoreTypes,
      chartData: getSummaryChartData(scoreTypes),
    };
    summaryCardsData.push(summaryCardData);
  });

  return summaryCardsData;
}

function ReviewManageSummary({ selectedReviewCycle, gridStatePersistenceKey }) {
  const [summaryStats, setSummaryStats] = useState([]);
  const [summaryStatsLoading, setSummaryStatsLoading] = useState(false);
  const selectedCohort = useSelectedView();

  const fetchSummaryStats = () => {
    setSummaryStatsLoading(true);

    const requestReviewCycle = { ...selectedReviewCycle };
    delete requestReviewCycle.feedbackForms;

    getAggregatedCount({
      cohort: selectedCohort,
      groupByKeys: {
        uuid: null,
        keyName: 'employeeId',
        displayName: 'Individual',
        enabled: true,
      },
      source: requestReviewCycle,
    })
      .then((res) => {
        const summaryCardsData = createSummaryCardsData(res);
        setSummaryStats(summaryCardsData);
      })
      .catch((err) => {
        console.log(err);
        setSummaryStats([]);
      })
      .finally(() => {
        setSummaryStatsLoading(false);
      });
  };

  useEffect(() => {
    if (selectedReviewCycle) {
      fetchSummaryStats();
    }
  }, [selectedCohort, selectedReviewCycle]);

  function getStatTitle(stat) {
    if (stat?.title === 'Employee Review') {
      return 'Self Reviews';
    }

    // Add an 's' to the end of the title to make plural
    return `${stat?.title}s`;
  }

  // calculate the sum of values in a chartData array
  function sumChartData(chartData) {
    return chartData.reduce((sum, data) => sum + (data?.value ?? 0), 0);
  }
  if (summaryStatsLoading) {
    return <SkeletonLoader />;
  }

  if (!summaryStats || summaryStats.length === 0) {
    return null;
  }

  const cardKPIData = summaryStats?.map((stat) => ({
    cardId: getCardIdFromStatType(stat?.type),
    title: getStatTitle(stat),
    count: stat?.inProgress?.count || '0',
    subTitle: `Average score : ${stat?.averageScore === '-' ? 'NA' : stat?.averageScore}`,
    totalCount: sumChartData(stat.chartData),
  }));

  if (!cardKPIData?.length) {
    return null;
  }

  return (
    <MetricCards
      height="100%"
      columns={`repeat(${cardKPIData?.length}, 1fr)`}
      cardTitle="Metrics"
      cards={cardKPIData}
      module={MODULE_KEYS_METRIC_CARDS_DEER.REVIEW}
      gridStatePersistenceKey={gridStatePersistenceKey}
      cardStyle="style2"
    />
  );
}

export default ReviewManageSummary;
